import React from 'react';
import { Input, Textarea } from '@fluentui/react-components';
import { useDispatch, useSelector } from 'react-redux';
import {
    DeleteWorkstream,
    GetWorkstreams,
    GetWorkstreamsDefaults,
    PatchWorkstream,
    PostWorkstream,
} from '../../redux/dataroomSlice';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import checkboxCheck from './assets/checkbox-check.svg';
import { suggestWorkstreams } from './projectSetupApi';
import { buildItemsFromSuggestion } from './setupSuggestionMapping';
import DocumentContextBanner from './DocumentContextBanner';
import SetupSuggestionLoader from './SetupSuggestionLoader';

export const WORKSTREAM_CATALOG = [
    {
        name: 'Information Technology',
        description: 'Core workstream for assessing technology architecture, operations, scalability and delivery capability.',
        recommend: true,
    },
    {
        name: 'Cybersecurity',
        description: 'Recommended because cybersecurity is included in the confirmed project scope.',
        fallbackDescription: 'Cybersecurity posture, controls and risk assessment.',
        recommendWhenScope: 'Cybersecurity',
    },
    {
        name: 'Commercial',
        description: 'Relevant for understanding technology dependencies on customers, products and commercial growth.',
        recommend: true,
    },
    { name: 'Financial', description: 'Financial review of accounts and key metrics.' },
    { name: 'Legal', description: 'Legal structure, contracts and IP review.' },
    { name: 'Human Resources', description: 'HR structure, headcount and talent review.' },
    { name: 'Operations', description: 'Operational processes and efficiency review.' },
    { name: 'ESG', description: 'Environmental, social and governance assessment.' },
    { name: 'IT M&A Integration', description: 'Integration planning for IT systems post-transaction.' },
];

const nextKey = () => `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const PROJECT_CONTEXT_WORKSTREAM_NAME = 'Project Context';

function visibleWorkstreams(workstreams = []) {
    return (workstreams || []).filter(ws => (ws?.name || '').trim() !== PROJECT_CONTEXT_WORKSTREAM_NAME);
}

function isRecommendedEntry(entry, scopeAreas) {
    if (entry.recommend) return true;
    if (entry.recommendWhenScope && (scopeAreas || []).includes(entry.recommendWhenScope)) return true;
    return false;
}

function buildItems({ existing = [], defaultWorkstreams = [], scopeAreas = [] }) {
    const existingByName = new Map((existing || []).map(ws => [ws.name, ws]));
    const items = WORKSTREAM_CATALOG.map(entry => {
        const recommended = isRecommendedEntry(entry, scopeAreas);
        return {
            key: entry.name,
            name: entry.name,
            description: recommended ? entry.description : entry.fallbackDescription || entry.description,
            recommended,
            selected: recommended,
            existingId: existingByName.get(entry.name)?.id,
            originalName: existingByName.get(entry.name)?.name || entry.name,
        };
    });

    (defaultWorkstreams || []).forEach(ws => {
        const name = (ws.workstream || ws.name || '').trim();
        if (!name || items.some(item => item.name === name)) return;
        items.push({
            key: name,
            name,
            description: '',
            recommended: false,
            selected: false,
            existingId: existingByName.get(name)?.id,
            originalName: existingByName.get(name)?.name || name,
        });
    });

    (existing || []).forEach(ws => {
        if (!ws?.name || items.some(item => item.name === ws.name || item.existingId === ws.id)) return;
        items.push({
            key: `existing-${ws.id}`,
            name: ws.name,
            description: '',
            recommended: false,
            selected: false,
            existingId: ws.id,
            originalName: ws.name,
        });
    });

    return items;
}

const SetupWorkstreams = ({
    project,
    scopeAreas,
    onContinue,
    onExit,
    documentBanner,
    onDismissDocumentBanner,
    onRefreshRecommendations,
    refreshingRecommendations = false,
    onRefreshSettled,
    refreshToken = 0,
}) => {
    const dispatch = useDispatch();
    const selectedProject = useSelector(state => state?.projectData?.selectedProject);
    const [items, setItems] = React.useState([]);
    const [ready, setReady] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [editingKey, setEditingKey] = React.useState(null);
    const [draftName, setDraftName] = React.useState('');
    const [draftDescription, setDraftDescription] = React.useState('');
    const [addingCustom, setAddingCustom] = React.useState(false);
    const [customName, setCustomName] = React.useState('');
    const [customDescription, setCustomDescription] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        setReady(false);
        const load = async () => {
            const [existingRes, defaultsRes] = await Promise.all([
                dispatch(GetWorkstreams(project?.id)),
                dispatch(GetWorkstreamsDefaults()),
            ]);
            if (cancelled) return;
            const existing = visibleWorkstreams(existingRes?.payload?.data || []);
            const defaultWorkstreams = defaultsRes?.payload?.data || [];
            let suggestion = null;
            try {
                suggestion = await suggestWorkstreams(project?.id);
            } catch {
                suggestion = null;
            }
            if (cancelled) return;
            if (suggestion?.recommended_workstreams?.length || suggestion?.additional_relevant_workstreams?.length) {
                setItems(buildItemsFromSuggestion(suggestion, existing));
            } else {
                setItems(buildItems({ existing, defaultWorkstreams, scopeAreas }));
            }
            setReady(true);
            onRefreshSettled?.();
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, project?.id, refreshToken, scopeAreas]);

    const updateItem = (key, patch) => {
        setItems(prev => prev.map(item => (item.key === key ? { ...item, ...patch } : item)));
    };

    const startEdit = item => {
        setAddingCustom(false);
        setEditingKey(item.key);
        setDraftName(item.name);
        setDraftDescription(item.description || '');
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setDraftName('');
        setDraftDescription('');
    };

    const saveEdit = () => {
        const name = draftName.trim();
        if (!name) return;
        const duplicate = items.some(item => item.key !== editingKey && item.name.toLowerCase() === name.toLowerCase());
        if (duplicate) return;
        updateItem(editingKey, { name, description: draftDescription.trim() });
        cancelEdit();
    };

    const addCustom = () => {
        const name = customName.trim();
        if (!name) return;
        if (items.some(item => item.name.toLowerCase() === name.toLowerCase())) return;
        setItems(prev => [
            ...prev,
            {
                key: nextKey(),
                name,
                description: customDescription.trim(),
                recommended: false,
                selected: true,
            },
        ]);
        setCustomName('');
        setCustomDescription('');
        setAddingCustom(false);
    };

    const persist = async () => {
        const projectId = project?.id || selectedProject?.id;
        const vdrId = project?.vdr?.id || selectedProject?.vdr?.id;
        for (const item of items) {
            if (item.name === PROJECT_CONTEXT_WORKSTREAM_NAME) continue;
            if (!item.selected && item.existingId) {
                const result = await dispatch(DeleteWorkstream({ id: item.existingId, suppressSuccessToast: true }));
                if (result?.type !== 'DELETE_DATAROOM_WORKSTREAM/fulfilled') {
                    return false;
                }
            }
        }
        for (const item of items) {
            if (item.name === PROJECT_CONTEXT_WORKSTREAM_NAME) continue;
            if (!item.selected) continue;
            if (item.existingId) {
                const result = await dispatch(
                    PatchWorkstream({
                        id: item.existingId,
                        name: item.name,
                        description: item.description || '',
                        project: projectId,
                        suppressSuccessToast: true,
                    }),
                );
                if (result?.type !== 'PATCH_DATAROOM_WORKSTREAM/fulfilled') {
                    return false;
                }
            } else {
                const result = await dispatch(
                    PostWorkstream({
                        name: item.name,
                        description: item.description || '',
                        project: projectId,
                        vdr: vdrId,
                        suppressSuccessToast: true,
                    }),
                );
                if (result?.type !== 'POST_DATAROOM_WORKSTREAM/fulfilled') {
                    return false;
                }
            }
        }
        const listRes = await dispatch(GetWorkstreams(projectId));
        if (listRes?.type !== 'GET_DATAROOM_WORKSTREAM/fulfilled') {
            return false;
        }
        const list = listRes?.payload?.data || [];
        const selected = items
            .filter(item => item.selected)
            .map(item => {
                const match = item.existingId
                    ? list.find(ws => ws.id === item.existingId) || list.find(ws => ws.name === item.name)
                    : list.find(ws => ws.name === item.name);
                return match ? { id: match.id, name: match.name, description: item.description || match.description || '', acl: match.acl } : null;
            })
            .filter(Boolean);
        return selected.length ? selected : false;
    };

    const handleConfirm = async () => {
        if (saving) return;
        setSaving(true);
        const selected = await persist();
        setSaving(false);
        if (selected) onContinue(selected);
    };

    const handleExit = async () => {
        if (saving) return;
        setSaving(true);
        const ok = await persist();
        setSaving(false);
        if (ok) onExit();
    };

    const recommended = items.filter(item => item.recommended);
    const additional = items.filter(item => !item.recommended);
    const selectedCount = items.filter(item => item.selected).length;
    const continueDisabled = !ready || saving || selectedCount === 0 || Boolean(editingKey);

    return (
        <NewProjectWizardCard
            step={1}
            totalSteps={4}
            wide
            title="Set up workstreams"
            subtitle="Based on the project context, Lens recommends the following workstreams. Review them before applying."
            headerAside={<span className={styles.suggestedBadge}>Suggested by Lens</span>}
            continueLabel="Confirm workstreams"
            continueDisabled={continueDisabled}
            continueLoading={saving}
            onContinue={handleConfirm}
            onSkip={handleExit}
            skipLabel="Save & exit"
        >
            {documentBanner ? (
                <DocumentContextBanner
                    variant={documentBanner}
                    onDismiss={onDismissDocumentBanner}
                    onRefresh={onRefreshRecommendations}
                    refreshing={refreshingRecommendations}
                    onNotNow={onDismissDocumentBanner}
                />
            ) : null}
            {!ready ? (
                <SetupSuggestionLoader label="Loading recommended workstreams…" />
            ) : (
                <>
            <p className={styles.scopeGroupLabel}>Recommended</p>
            <div className={styles.wsList}>
                {recommended.map(item => (
                    <WorkstreamRow
                        key={item.key}
                        item={item}
                        editing={editingKey === item.key}
                        draftName={draftName}
                        draftDescription={draftDescription}
                        onDraftName={setDraftName}
                        onDraftDescription={setDraftDescription}
                        onToggle={() => updateItem(item.key, { selected: !item.selected })}
                        onEdit={() => startEdit(item)}
                        onSave={saveEdit}
                        onCancel={cancelEdit}
                    />
                ))}
            </div>

            <p className={`${styles.scopeGroupLabel} ${styles.wsAdditionalLabel}`}>Additional workstreams</p>
            <div className={styles.wsList}>
                {additional.map(item => (
                    <WorkstreamRow
                        key={item.key}
                        item={item}
                        editing={editingKey === item.key}
                        draftName={draftName}
                        draftDescription={draftDescription}
                        onDraftName={setDraftName}
                        onDraftDescription={setDraftDescription}
                        onToggle={() => updateItem(item.key, { selected: !item.selected })}
                        onEdit={() => startEdit(item)}
                        onSave={saveEdit}
                        onCancel={cancelEdit}
                    />
                ))}
            </div>

            {addingCustom ? (
                <div className={styles.wsCustomForm}>
                    <Input
                        className={styles.textInput}
                        value={customName}
                        onChange={e => setCustomName(e.target.value)}
                        placeholder="Workstream name"
                        autoFocus
                    />
                    <Textarea
                        className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                        value={customDescription}
                        onChange={e => setCustomDescription(e.target.value)}
                        placeholder="Description (optional)"
                        rows={2}
                        resize="none"
                    />
                    <div className={styles.wsEditActions}>
                        <button type="button" className={styles.primaryButton} disabled={!customName.trim()} onClick={addCustom}>
                            Add workstream
                        </button>
                        <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => {
                                setAddingCustom(false);
                                setCustomName('');
                                setCustomDescription('');
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <button type="button" className={styles.addScopeLink} onClick={() => setAddingCustom(true)}>
                    + Add custom workstream
                </button>
            )}
                </>
            )}
        </NewProjectWizardCard>
    );
};

const WorkstreamRow = ({
    item,
    editing,
    draftName,
    draftDescription,
    onDraftName,
    onDraftDescription,
    onToggle,
    onEdit,
    onSave,
    onCancel,
}) => {
    const selected = item.selected;
    if (editing) {
        return (
            <div className={`${styles.wsRow} ${styles.wsRowSelected}`}>
                <div className={styles.wsEditFields}>
                    <Input className={styles.textInput} value={draftName} onChange={e => onDraftName(e.target.value)} />
                    <Textarea
                        className={`${styles.textareaInput} ${styles.wsDescriptionInput}`}
                        value={draftDescription}
                        onChange={e => onDraftDescription(e.target.value)}
                        rows={2}
                        resize="none"
                    />
                    <div className={styles.wsEditActions}>
                        <button type="button" className={styles.primaryButton} disabled={!draftName.trim()} onClick={onSave}>
                            Save
                        </button>
                        <button type="button" className={styles.ghostButton} onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.wsRow} ${selected ? styles.wsRowSelected : styles.wsRowIdle}`}>
            <button
                type="button"
                role="checkbox"
                aria-checked={selected}
                className={`${styles.wsCheckbox} ${selected ? styles.wsCheckboxChecked : ''}`}
                onClick={onToggle}
            >
                {selected ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
            </button>
            <div className={styles.wsCopy}>
                <div className={styles.wsTitleRow}>
                    <div className={styles.wsTitleGroup}>
                        <span className={styles.wsName}>{item.name}</span>
                        {item.recommended ? <span className={styles.wsRecommendedBadge}>Recommended</span> : null}
                    </div>
                    <button type="button" className={styles.summaryEdit} onClick={onEdit}>
                        Edit
                    </button>
                </div>
                {item.description ? <p className={styles.wsDescription}>{item.description}</p> : null}
                {item.reason ? <p className={styles.irlWhy}>{item.reason}</p> : null}
            </div>
        </div>
    );
};

export default SetupWorkstreams;
